/**
 * Plans sub-tab (v2 — DNKADM-0056 U1) — the Membership Plans list + Add/Edit dialog +
 * per-plan credit-deposit editor (handoff §5.9).
 *
 * **A plan IS the tier.** Loads `listMembershipPlans({ status })`
 * through `useResource`, keyed on the Active/Inactive/All filter (`useActiveFilter` +
 * `<ActiveFilter>`, §4.3). A table of name (the tier) → type (Sellable/Comp, from
 * `price > 0`) → who can buy (Members and staff / Staff only, from `staffOnly`,
 * DNKADM-0381) → term → price → status, each row with Edit, Copy (duplicate), a
 * who-can-buy flip (a whole-row PUT via `setMembershipPlanStaffOnly`), and a
 * status-gated lifecycle action: Publish (Draft), Deactivate (Active), or Reactivate
 * (Inactive) (§4.3, DNKAPI-0261). The shown Status chip is the lifecycle `status`
 * (Draft/Active/Inactive), not the `isActive` boolean; who-can-buy is orthogonal to it and
 * the Active/Inactive/All filter keeps meaning lifecycle only. The per-bucket **credit deposits**
 * are edited inside the {@link PlanDialog} (live on edit / staged-then-attached on create,
 * mirroring the Package dialog) — the old row-expand chevron editor is retired. "Add plan"
 * + Edit open the dialog. Every write refetches + toasts. A first-run empty tenant (§8)
 * shows a strong empty state + CTA.
 *
 * A **Members** column (DNKADM-0407 U4) shows `impact.activeMemberships` (DNKAPI-0562) — a dash
 * when the server sends no `impact`.
 *
 * **Toolbar, sort and URL (DNKADM-0430 P1-F).** The shared {@link ListToolbar} holds a plan
 * name search and the Active/Inactive/All filter; every data column has a shared
 * {@link SortableHeader} (default Plan name ascending; unknown member counts sort last in
 * both directions). Search, filter and sort live in the URL (`plansListView.ts`).
 *
 * Money renders via the 0049 helpers (`formatMoney` with the tenant currency from
 * `useTenantCurrency`). No "GoHighLevel"/"GHL" reaches any rendered string.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { ListNoMatch } from '@/components/list/ListNoMatch';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SortableHeader } from '@/components/list/SortableHeader';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { useListUrlState } from '@/components/list/useListUrlState';
import { Icon, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useUrlActiveFilter } from '@/hooks/useActiveFilter';
import { ActiveFilter } from '@/components/ActiveFilter';
import { useToast } from '@/components/useToast';
import { useTenantCurrency } from '@/hooks/useTenantCurrency';
import { serverMessage } from '@/api/serverMessage';
import { formatMoney } from '@/utils/v2Format';
import {
  copyMembershipPlan,
  deactivateMembershipPlan,
  listMembershipPlans,
  publishMembershipPlan,
  reactivateMembershipPlan,
  setMembershipPlanStaffOnly,
  type MembershipPlan,
} from '@/api/membershipPlans';
import { PlanDialog } from './PlanDialog';
import {
  PLAN_DEFAULT_SORT,
  PLAN_SORT_KEYS,
  PLANS_URL_DEFAULTS,
  planAudienceLabel,
  planMatches,
  planName,
  planSortValue,
  planStatusLabel,
  planTypeLabel,
  type PlanSortKey,
} from './plansListView';
import { ScreenError } from '@/components/ScreenError';

export function PlansTab() {
  const t = resolveTokens(useTheme());
  const showToast = useToast();
  // DNKADM-0430: search, status filter and sort come from the URL (`plansListView.ts`).
  const url = useListUrlState(PLANS_URL_DEFAULTS);
  const setUrl = url.set;
  const filter = useUrlActiveFilter(url.values.status, setUrl);
  const search = url.values.q;
  const sort = parseSort(url.values.sort, url.values.dir, PLAN_SORT_KEYS, PLAN_DEFAULT_SORT);
  const { currency } = useTenantCurrency();

  const plans = useResource(
    useCallback(() => listMembershipPlans({ status: filter.status }), [filter.status]),
    [filter.status],
  );

  // Dialog state: `null` = closed; `{ plan: null }` = add; `{ plan }` = edit. The
  // credit-deposit editor now lives inside the dialog (Edit opens it), not a row chevron.
  const [dialog, setDialog] = useState<{ plan: MembershipPlan | null } | null>(null);

  const refetch = plans.refetch;

  const handleDeactivate = useCallback(
    async (plan: MembershipPlan) => {
      try {
        await deactivateMembershipPlan(plan.id);
        refetch();
        showToast('Plan deactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t deactivate the plan.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handleReactivate = useCallback(
    async (plan: MembershipPlan) => {
      try {
        await reactivateMembershipPlan(plan.id);
        refetch();
        showToast('Plan reactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t reactivate the plan.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handlePublish = useCallback(
    async (plan: MembershipPlan) => {
      try {
        await publishMembershipPlan(plan.id);
        refetch();
        showToast('Plan published', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t publish the plan.', 'error');
      }
    },
    [refetch, showToast],
  );

  // DNKADM-0381 U1: flip who can buy from the row. A whole-row PUT (the same write as
  // Edit → Save with only `staffOnly` changed); the refetch shows the new column value.
  const handleStaffOnly = useCallback(
    async (plan: MembershipPlan, staffOnly: boolean) => {
      try {
        await setMembershipPlanStaffOnly(plan, staffOnly);
        refetch();
        showToast(
          staffOnly ? 'Plan is now staff only' : 'Plan is now open to members and staff',
          'success',
        );
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t change who can buy the plan.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handleCopy = useCallback(
    async (plan: MembershipPlan) => {
      try {
        await copyMembershipPlan(plan.id);
        refetch();
        showToast('Plan duplicated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t duplicate the plan.', 'error');
      }
    },
    [refetch, showToast],
  );

  function priceLabel(plan: MembershipPlan): string {
    return currency ? formatMoney(plan.price, currency) : plan.price.toFixed(2);
  }

  const rows = plans.data;
  const sortedRows = useMemo(
    () =>
      sortRows(
        (rows ?? []).filter((plan) => planMatches(plan, search)),
        (plan) => planSortValue(plan, sort.key),
        sort.dir,
        (plan) => plan.id,
      ),
    [rows, search, sort.key, sort.dir],
  );
  const onSort = useCallback(
    (key: PlanSortKey) => setUrl(toSortParams(toggleSort(sort, key), PLAN_DEFAULT_SORT)),
    [setUrl, sort],
  );
  const isFirstRun = useMemo(
    () => filter.value === 'active' && rows !== null && rows.length === 0,
    [filter.value, rows],
  );

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Each plan is a membership tier. Set its term, price, where it’s available, and the
          credits it deposits in each bucket on purchase or renewal.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Icon name="add" />}
          onClick={() => setDialog({ plan: null })}
          sx={{ flexShrink: 0 }}
        >
          Add plan
        </Button>
      </Stack>

      <ListToolbar
        search={{
          value: search,
          onChange: (next) => setUrl({ q: next }),
          placeholder: 'Search plans',
          disabled: plans.status !== 'ready',
        }}
        filters={
          <ActiveFilter
            value={filter.value}
            onChange={filter.setValue}
            aria-label="Filter plans by status"
          />
        }
        onClear={() => setUrl({ q: null, status: null })}
        canClear={search.length > 0 || filter.value !== 'active'}
      />

      {plans.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={260}
          role="status"
          aria-label="Loading membership plans"
          sx={{ borderRadius: 3 }}
        />
      )}

      {plans.status === 'error' && (
        <ScreenError
          error={plans.error}
          what="the membership plans"
          onRetry={plans.refetch}
          sx={{ alignItems: 'center' }}
        />
      )}

      {plans.status === 'ready' && rows && rows.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: t.lineStrong,
            borderRadius: 3,
          }}
        >
          <Icon name="card_membership" sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography sx={{ mt: 1, fontWeight: 600 }}>
            {isFirstRun ? 'No membership plans yet' : 'No plans match this filter'}
          </Typography>
          {isFirstRun && (
            <>
              <Typography variant="body2" sx={{ mt: 0.5, mb: 2.5, color: 'text.secondary' }}>
                Add your first plan — a tier with its term and price — then set the credits it
                deposits in each bucket.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Icon name="add" />}
                onClick={() => setDialog({ plan: null })}
              >
                Add plan
              </Button>
            </>
          )}
        </Paper>
      )}

      {plans.status === 'ready' && rows && rows.length > 0 && (
        <TableContainer>
          <Table aria-label="Membership plans" size="small">
            <TableHead>
              <TableRow>
                <SortableHeader label="Plan (tier)" col="name" sort={sort} onSort={onSort} />
                <SortableHeader label="Type" col="type" sort={sort} onSort={onSort} />
                <SortableHeader label="Who can buy" col="audience" sort={sort} onSort={onSort} />
                <SortableHeader label="Term" col="term" align="right" sort={sort} onSort={onSort} />
                <SortableHeader
                  label="Price"
                  col="price"
                  align="right"
                  sort={sort}
                  onSort={onSort}
                />
                <SortableHeader
                  label="Members"
                  col="members"
                  align="right"
                  sort={sort}
                  onSort={onSort}
                />
                <SortableHeader label="Status" col="status" sort={sort} onSort={onSort} />
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((plan) => {
                const name = planName(plan);
                return (
                  <TableRow key={plan.id}>
                    <TableCell>{name}</TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={planTypeLabel(plan)} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={planAudienceLabel(plan)} />
                    </TableCell>
                    <TableCell align="right">{plan.termDays} days</TableCell>
                    <TableCell align="right">{priceLabel(plan)}</TableCell>
                    <TableCell align="right">{plan.impact?.activeMemberships ?? '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={planStatusLabel(plan)}
                        color={
                          plan.status === 'DRAFT'
                            ? 'warning'
                            : plan.status === 'INACTIVE'
                              ? 'default'
                              : 'success'
                        }
                        variant={plan.status === 'ACTIVE' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconAction
                        size="small"
                        label={`Edit plan ${name}`}
                        onClick={() => setDialog({ plan })}
                      >
                        <Icon name="edit" />
                      </IconAction>
                      <IconAction
                        size="small"
                        label={`Duplicate plan ${name}`}
                        onClick={() => void handleCopy(plan)}
                      >
                        <Icon name="content_copy" />
                      </IconAction>
                      <IconAction
                        size="small"
                        label={
                          plan.staffOnly
                            ? `Open plan ${name} to members`
                            : `Make plan ${name} staff only`
                        }
                        onClick={() => void handleStaffOnly(plan, !plan.staffOnly)}
                      >
                        <Icon name={plan.staffOnly ? 'visibility' : 'visibility_off'} />
                      </IconAction>
                      {plan.status === 'DRAFT' && (
                        <IconAction
                          size="small"
                          label={`Publish plan ${name}`}
                          onClick={() => void handlePublish(plan)}
                        >
                          <Icon name="publish" />
                        </IconAction>
                      )}
                      {plan.status === 'ACTIVE' && (
                        <IconAction
                          size="small"
                          label={`Deactivate plan ${name}`}
                          onClick={() => void handleDeactivate(plan)}
                        >
                          <Icon name="toggle_off" />
                        </IconAction>
                      )}
                      {plan.status === 'INACTIVE' && (
                        <IconAction
                          size="small"
                          label={`Reactivate plan ${name}`}
                          onClick={() => void handleReactivate(plan)}
                        >
                          <Icon name="toggle_on" />
                        </IconAction>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {plans.status === 'ready' && rows && rows.length > 0 && sortedRows.length === 0 && (
        <ListNoMatch noun="plans" query={search} />
      )}

      <PlanDialog
        open={dialog !== null}
        plan={dialog?.plan ?? null}
        currency={currency}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          refetch();
        }}
        showToast={showToast}
      />
    </Box>
  );
}
